import { BlockOutlined, CheckCircle, ReceiptLongOutlined } from '@mui/icons-material';
import { alpha, Box, Typography } from '@mui/material';
import { BLANK_OPTION, BlankettOption } from 'src/modules/sending/functions/blankettOptions';

interface BlankettPickerProps {
  // Vilka blanketter som får väljas. Utskicksbeställningen begränsar till fakturagruppens
  // kontotyp, fakturamallen visar hela listan för SuperAdmin.
  options: BlankettOption[];
  value: string;
  onChange: (code: string) => void;
  // Låst läge: valen går inte att ändra och de ovalda tonas ned.
  locked?: boolean;
  // Grön ton på det valda kortet, används när valet är klarmarkerat.
  done?: boolean;
}

// Val av fakturablankett som alternativkort, samma utseende som val av leveransmetod.
export const BlankettPicker = ({ options, value, onChange, locked = false, done = false }: BlankettPickerProps) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    {options.map((o) => {
      const selected = value === o.code;
      // Vald + klarmarkerad = grön (som övriga klara rutor); vald men ej klar = primärfärg; annars neutral.
      const isDone = selected && done;
      const accent = isDone ? 'success.main' : 'primary.main';
      const OptIcon = o.code === BLANK_OPTION.code ? BlockOutlined : ReceiptLongOutlined;

      return (
        <Box
          key={o.code}
          role='button'
          onClick={() => !locked && onChange(o.code)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            border: '2px solid',
            borderColor: selected ? accent : 'divider',
            borderRadius: 2,
            // Grön ton = samma som övriga klara kort i guiden (cardStyle): alpha(success.light, 0.2).
            bgcolor: (theme) => {
              if (isDone) return alpha(theme.palette.success.light, 0.2);
              if (selected) return theme.palette.action.selected;
              return theme.palette.background.paper;
            },
            cursor: locked ? 'default' : 'pointer',
            opacity: locked && !selected ? 0.5 : 1,
            transition: 'border-color .15s, background-color .15s',
            '&:hover': locked ? {} : { borderColor: 'primary.main' },
          }}
        >
          <OptIcon sx={{ fontSize: 30, color: selected ? accent : 'action.active' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }}>{o.label}</Typography>
            <Typography variant='body2' color='text.secondary'>
              {o.description}
            </Typography>
          </Box>
          {selected && <CheckCircle sx={{ ml: 'auto', color: accent }} />}
        </Box>
      );
    })}
  </Box>
);
